/**
 * Reads DataFast tracking cookies set by the script loaded in _app.tsx.
 * Forwarded to Stripe checkout sessions as `metadata` for revenue attribution.
 * See: https://datafa.st/docs/guides/stripe-revenue-tracking
 */
export function getDataFastIds(): {
  DataFastVisitorId?: string;
  DataFastSessionId?: string;
} {
  if (typeof document === 'undefined') return {};

  const cookies = Object.fromEntries(
    document.cookie
      .split('; ')
      .filter(Boolean)
      .map((c) => {
        const idx = c.indexOf('=');
        const key = idx === -1 ? c : c.slice(0, idx);
        const value = idx === -1 ? '' : decodeURIComponent(c.slice(idx + 1));
        return [key, value];
      })
  );

  const result: { DataFastVisitorId?: string; DataFastSessionId?: string } = {};
  if (cookies['datafast_visitor_id']) result.DataFastVisitorId = cookies['datafast_visitor_id'];
  if (cookies['datafast_session_id']) result.DataFastSessionId = cookies['datafast_session_id'];
  return result;
}

/**
 * Just the visitor id, for callers that persist it rather than attach it to a
 * Stripe session. The session id is deliberately left out: it rotates, so it is
 * meaningless once stored, and the org endpoint has no field for it.
 */
export function getDataFastVisitorId(): { DataFastVisitorId?: string } {
  const { DataFastVisitorId } = getDataFastIds();
  return DataFastVisitorId ? { DataFastVisitorId } : {};
}

/**
 * The DataFast browser tracker, as installed by the script tag in _app.tsx and
 * stubbed ahead of it by the `datafast-queue` shim in _document.tsx. Calls made
 * before the real tracker loads are buffered on `.q` and drained by it.
 */
type DataFastFn = (action: string, payload?: Record<string, unknown>) => void;

function getDataFast(): DataFastFn | null {
  if (typeof window === 'undefined') return null;
  const fn = (window as unknown as { datafast?: DataFastFn }).datafast;
  return typeof fn === 'function' ? fn : null;
}

/**
 * DataFast rejects anything outside [a-z0-9_-] in a property name, caps a value
 * at 255 characters, and takes at most 10 properties per call. Sending more than
 * that drops the whole call, so the payload is trimmed here rather than at each
 * call site. Empty values are dropped: DataFast keeps the previous value for a
 * key it does not receive, which beats overwriting a real name with "".
 */
const DATAFAST_MAX_PROPERTIES = 10;
const DATAFAST_MAX_VALUE_LENGTH = 255;

export function sanitizeDataFastProperties(
  properties: Record<string, unknown>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (Object.keys(out).length >= DATAFAST_MAX_PROPERTIES) break;
    if (value === undefined || value === null || value === '') continue;
    if (!/^[a-z0-9_-]{1,64}$/.test(key)) continue;
    out[key] = String(value).slice(0, DATAFAST_MAX_VALUE_LENGTH);
  }
  return out;
}

/**
 * Attach the current DataFast visitor to a known user, so the anonymous
 * pageviews recorded before sign-in are credited to that person.
 * `userId` is the Clerk user id. Never call this for an unverified session.
 */
export function datafastIdentify(userId: string, properties: Record<string, unknown> = {}): void {
  const datafast = getDataFast();
  if (!datafast || !userId) return;
  try {
    datafast('identify', { user_id: userId, ...sanitizeDataFastProperties(properties) });
  } catch {
    // Analytics must never break the app. A blocked tracker is not an error.
  }
}

/**
 * The goals this app reports, and the only names the DataFast funnel matches on.
 *
 * The funnel spans three repos, so no shared constant is possible. It is written
 * down in one place, `scripts/create_datafast_funnel.py` in the backend repo,
 * which provisions the funnel from these names. Renaming one here without
 * renaming it there breaks a funnel step silently: the step just reports zero.
 *
 * Steps 1, 2, 4 and 6 of that funnel live elsewhere:
 *   1. pageview /          (landing)
 *   2. pageview /sign-up   (reached the form)
 *   3. user_signed_up      (backend, org created)
 *   4. checkout_started    (here)
 *   5. free_trial_started  (backend, card added)
 *   6. subscription_activated (backend, trial converted)
 */
export const DATAFAST_GOAL = {
  /**
   * Stripe handed back a real checkout session, so the card step is on screen.
   * The rung between "made an account" and "gave us a card", which is where
   * self-serve signup bleeds and which no webhook can see.
   */
  checkoutStarted: 'checkout_started',
} as const;

/**
 * Record a custom goal. Backend-confirmed steps (signup, trial, activation) are
 * sent server-side instead (see shared/analytics/datafast.py in the backend),
 * so this is for things only the browser can witness.
 */
export function datafastGoal(name: string, properties: Record<string, unknown> = {}): void {
  const datafast = getDataFast();
  if (!datafast || !name) return;
  try {
    datafast(name, sanitizeDataFastProperties(properties));
  } catch {
    // Analytics must never break the app. A blocked tracker is not an error.
  }
}

/**
 * Step 4 of the funnel: the checkout is actually on screen.
 *
 * Called from `trackCheckoutStarted` in bily-tracking.tsx rather than from the
 * three pages that open a checkout, so the two trackers can never disagree
 * about what "checkout started" means and a fourth call site cannot forget it.
 */
export function trackDataFastCheckoutStarted(context: string, checkoutId?: string): void {
  datafastGoal(DATAFAST_GOAL.checkoutStarted, {
    checkout_context: context,
    checkout_id: checkoutId,
  });
}
